import {
  RoleAssignmentAction,
  RoleAssignmentActionArray,
  UserRole,
  UserRoleArray
} from '../models/generated-models';

export {RoleAssignmentAction, RoleAssignmentActionArray, UserRole, UserRoleArray};

export const STAFF_ROLES: UserRole[] = [
  'SUPER_ADMIN',
  'ADMIN',
  'OFFICER',
  'ACFS_OFFICER',
  'SC_OFFICER',
  'EXTERNAL_OFFICER'
];

export const BUSINESS_ROLES: UserRole[] = [
  'FARMER',
  'MIDDLEMAN',
  'EXPORTER',
  'AGENT_OPERATOR',
  'FARMER_AGENT',
  'MIDDLEMAN_AGENT'
];

/**
 * Sidebar menu visibility per the role matrix
 * ("User ระบบ มกอช. - การมองเห็นเมนู Supply Chain", 2026-09-14).
 * Dashboard = หน้าหลัก, profile = โปรไฟล์, purchaseSale = บันทึกซื้อ-ขาย,
 * qrTrace = ยืนยันการขาย (QR Trace), chat = แชท, survey = แบบสอบถาม,
 * businessTypeSettings = ตั้งค่าประเภทธุรกิจ (work item 198).
 * "กลับไปที่ e-Portal" stays visible to everyone.
 */
export const MENU_ROLE_SETS: Record<'dashboard' | 'profile' | 'purchaseSale' | 'observations' | 'qrTrace' | 'chat' | 'survey' | 'businessTypeSettings', UserRole[]> = {
  // Onboarding no longer assigns business roles, so plain USER sees the same
  // business menus FARMER used to see. Officers-only report stays locked.
  dashboard: [...STAFF_ROLES, 'USER', 'FARMER', 'FARMER_AGENT', 'MIDDLEMAN', 'MIDDLEMAN_AGENT'],
  profile: ['USER', 'FARMER', 'MIDDLEMAN', 'EXPORTER', 'AGENT_OPERATOR', 'FARMER_AGENT', 'MIDDLEMAN_AGENT'],
  purchaseSale: [...STAFF_ROLES, 'USER', ...BUSINESS_ROLES],
  /** รายงานข้อสังเกต — officers only; business users must never see it. */
  observations: [...STAFF_ROLES],
  qrTrace: ['SUPER_ADMIN', 'ADMIN', 'SC_OFFICER', 'USER', 'FARMER', 'FARMER_AGENT'],
  chat: ['SUPER_ADMIN', 'ADMIN', 'OFFICER', 'ACFS_OFFICER', 'SC_OFFICER', 'USER', ...BUSINESS_ROLES],
  survey: [...STAFF_ROLES, 'USER', ...BUSINESS_ROLES],
  /** ตั้งค่าประเภทธุรกิจ — administrators only; the business roles' sidebar unlock must not open it. */
  businessTypeSettings: ['SUPER_ADMIN', 'ADMIN'],
};

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  SUPER_ADMIN: 'ผู้ดูแลระบบสูงสุด',
  ADMIN: 'ผู้ดูแลระบบ',
  USER: 'ผู้ใช้งานทั่วไป',
  OFFICER: 'เจ้าหน้าที่',
  EXTERNAL_OFFICER: 'เจ้าหน้าที่หน่วยรับรอง',
  ACFS_OFFICER: 'เจ้าหน้าที่ มกอช.',
  SC_OFFICER: 'เจ้าหน้าที่ ศสช.',
  FARMER: 'เกษตรกร',
  MIDDLEMAN: 'ล้ง / พ่อค้าคนกลาง',
  EXPORTER: 'ผู้ส่งออก',
  AGENT_OPERATOR: 'ตัวแทนดำเนินการ',
  FARMER_AGENT: 'ตัวแทนเกษตรกร',
  MIDDLEMAN_AGENT: 'ตัวแทน พ่อค้าคนกลาง'
};

export const ROLE_ASSIGNMENT_ACTION_LABELS: Record<RoleAssignmentAction, string> = {
  PURCHASE_SALE: 'บันทึกซื้อ-ขาย',
};

export function normalizeUserRole(value: unknown): UserRole | null {
  const candidate = String(value ?? '').trim().toUpperCase();
  return UserRoleArray.includes(candidate as UserRole) ? (candidate as UserRole) : null;
}

export function normalizeUserRoles(values: readonly unknown[] | null | undefined): UserRole[] {
  const result: UserRole[] = [];
  for (const value of values ?? []) {
    const normalized = normalizeUserRole(value);
    if (normalized && !result.includes(normalized)) {
      result.push(normalized);
    }
  }
  return result;
}

export function userRoleLabel(value: UserRole | string | null | undefined): string {
  if (!value) return '-';
  const role = normalizeUserRole(value);
  if (role && USER_ROLE_LABELS[role]) {
    return USER_ROLE_LABELS[role];
  }
  return '-';
}

export function formatUserRoleSummary(values: readonly unknown[] | null | undefined): string {
  const normalized = normalizeUserRoles(values).filter((r) => BUSINESS_ROLES.includes(r));
  if (normalized.length > 0) {
    return normalized
      .map((r) => userRoleLabel(r))
      .filter((label) => !!label && label !== '-')
      .join(', ');
  }
  return '';
}

/**
 * Sidebar business-type summary: unique type titles in a stable (sorted)
 * order so refreshes never reshuffle them — first 3 joined with ", " plus
 * an ellipsis when there are more. Returns '' when there are none.
 */
export function formatBusinessTypeSummary(values: readonly unknown[] | null | undefined): string {
  const unique = [...new Set(
    (values ?? []).map((v) => String(v ?? '').trim()).filter((v) => v.length > 0),
  )].sort((a, b) => a.localeCompare(b, 'th'));
  if (unique.length === 0) {
    return '';
  }
  const shown = unique.slice(0, 3).join(', ');
  return unique.length > 3 ? `${shown}, …` : shown;
}

export function roleAssignmentActionLabel(value: RoleAssignmentAction | string | null | undefined): string {
  if (!value) return '-';
  return ROLE_ASSIGNMENT_ACTION_LABELS[value as RoleAssignmentAction] ?? String(value);
}

/**
 * Roles exempt from sidebar role locking: the former onboarding choices
 * (FARMER, MIDDLEMAN, AGENT_OPERATOR, EXPORTER) always see every sidebar
 * entry, since onboarding no longer gates menus by role. Route-level guards
 * (menuRoleGuard) still enforce page access.
 */
export const SIDEBAR_UNLOCKED_ROLES: UserRole[] = ['FARMER', 'MIDDLEMAN', 'AGENT_OPERATOR', 'EXPORTER'];

export function hasSidebarUnlockedRole(values: readonly unknown[] | null | undefined): boolean {
  return normalizeUserRoles(values).some((r) => SIDEBAR_UNLOCKED_ROLES.includes(r));
}

export function defaultLandingPathForRoles(roles: readonly (UserRole | string)[] | null | undefined): string {
  const normalized = normalizeUserRoles(roles);
  // USER is in the dashboard menu set (so plain users see it after onboarding)
  // but must not count as dashboard access here — otherwise fresh USERs would
  // skip the onboarding funnel.
  const hasDashboardAccess = MENU_ROLE_SETS.dashboard.some((r) => r !== 'USER' && normalized.includes(r));
  if (!hasDashboardAccess) {
    // AGENT_OPERATOR only has the profile menu; EXPORTER has the
    // purchase-sale menu (/trade) but no dashboard, so both land on /profile.
    if (normalized.includes('AGENT_OPERATOR')) {
      return '/profile';
    }
    if (normalized.includes('EXPORTER')) {
      return '/profile';
    }
    // Plain USER (no business role yet) goes through onboarding to complete
    // personal info (role stays USER); the portal sync guard runs there first.
    if (normalized.includes('USER')) {
      return '/profile/onboarding';
    }
  }
  return '/admin/dashboard';
}
